'use server'

import { randomBytes } from 'crypto'
import { getPayloadClient } from '../payload'
import { applicationSchema, contactSchema, CV_MAX_BYTES, quoteSchema, toFieldErrors, type FormState } from './schemas'
import { checkFormToken, detectCvType, rateLimit, verifyTurnstile } from './security'

const GENERIC_ERROR =
  'Votre demande n’a pas pu être enregistrée en raison d’un problème technique. Merci de réessayer dans quelques instants ou de nous écrire directement par e-mail.'

const asObject = (fd: FormData, keys: string[]) => Object.fromEntries(keys.map((k) => [k, (fd.get(k) ?? '').toString()]))

async function antiSpam(fd: FormData, scope: string): Promise<FormState | 'spam' | null> {
  // Champ piège : rempli uniquement par les robots. On simule un succès sans rien enregistrer.
  if ((fd.get('website') ?? '').toString() !== '') return 'spam'
  const token = checkFormToken(fd.get('_t'))
  if (token === 'too-fast' || token === 'invalid') return 'spam'
  if (token === 'expired') return { status: 'error', message: 'La page est restée ouverte trop longtemps. Rechargez-la puis renvoyez votre message.' }
  if (!(await rateLimit(scope))) {
    return { status: 'error', message: 'Trop de demandes ont été envoyées depuis votre connexion. Merci de réessayer dans quelques minutes.' }
  }
  if (!(await verifyTurnstile(fd.get('cf-turnstile-response')))) {
    return { status: 'error', message: 'La vérification anti-robot a échoué. Merci de réessayer.' }
  }
  return null
}

const smtpReady = () => Boolean(process.env.SMTP_HOST && process.env.SMTP_FROM)

async function notify(subject: string, lines: [string, string | undefined | null][], replyTo: string) {
  if (!smtpReady()) return false
  const payload = await getPayloadClient()
  let to = process.env.CONTACT_TO
  if (!to) {
    const s = await payload.findGlobal({ slug: 'parametres', depth: 0 })
    to = s.email || undefined
  }
  if (!to) return false
  const text = lines
    .filter(([, v]) => v)
    .map(([k, v]) => `${k} : ${v}`)
    .join('\n\n')
  try {
    await payload.sendEmail({ to, replyTo, subject, text: `${text}\n\n—\nMessage reçu via le site web EXELEC. Retrouvez-le dans l’administration.` })
    return true
  } catch (err) {
    payload.logger.error({ msg: '[formulaire] échec de la notification e-mail', code: (err as { code?: string }).code })
    return false
  }
}

export async function submitContact(fd: FormData): Promise<FormState> {
  const kind = fd.get('kind') === 'devis' ? 'devis' : 'contact'
  const spam = await antiSpam(fd, `contact-${kind}`)
  const okMessage =
    kind === 'devis'
      ? 'Votre demande de devis a bien été enregistrée. Notre équipe l’étudie et revient vers vous rapidement.'
      : 'Votre message a bien été enregistré. Notre équipe vous répondra dans les meilleurs délais.'
  if (spam === 'spam') return { status: 'success', message: okMessage }
  if (spam) return spam

  const parsed =
    kind === 'devis'
      ? quoteSchema.safeParse(asObject(fd, ['name', 'email', 'phone', 'organisation', 'role', 'projectType', 'budget', 'deadline', 'message', 'consent']))
      : contactSchema.safeParse(asObject(fd, ['name', 'email', 'phone', 'organisation', 'subject', 'message', 'consent']))
  if (!parsed.success) {
    return { status: 'error', message: 'Certains champs sont à corriger.', fieldErrors: toFieldErrors(parsed.error.issues) }
  }
  const d = parsed.data as Record<string, string>

  try {
    const payload = await getPayloadClient()
    const doc = await payload.create({
      collection: 'demandes',
      overrideAccess: true,
      data: {
        kind,
        name: d.name,
        email: d.email,
        phone: d.phone || undefined,
        organisation: d.organisation || undefined,
        role: d.role || undefined,
        subject: d.subject || (kind === 'devis' ? `Demande de devis — ${d.projectType}` : undefined),
        projectType: d.projectType || undefined,
        budget: d.budget || undefined,
        deadline: d.deadline || undefined,
        message: d.message,
        consentAt: new Date().toISOString(),
        treatment: 'nouveau',
      },
    })
    const sent = await notify(
      kind === 'devis' ? `[Site] Demande de devis — ${d.organisation}` : `[Site] Contact — ${d.subject}`,
      [
        ['Type', kind === 'devis' ? 'Demande de devis' : 'Contact'],
        ['Nom', d.name],
        ['E-mail', d.email],
        ['Téléphone', d.phone],
        ['Organisation', d.organisation],
        ['Fonction', d.role],
        ['Objet', d.subject],
        ['Type de projet', d.projectType],
        ['Budget indicatif', d.budget],
        ['Échéance', d.deadline],
        ['Message', d.message],
      ],
      d.email,
    )
    if (sent) await payload.update({ collection: 'demandes', id: doc.id, overrideAccess: true, data: { emailNotified: true } })
    return { status: 'success', message: okMessage }
  } catch (err) {
    const payload = await getPayloadClient()
    payload.logger.error({ msg: '[formulaire] enregistrement impossible', err: (err as Error).name })
    return { status: 'error', message: GENERIC_ERROR }
  }
}

export async function submitApplication(fd: FormData): Promise<FormState> {
  const okMessage = 'Votre candidature a bien été enregistrée. Nous l’étudierons avec attention et reviendrons vers vous si votre profil correspond à nos besoins.'
  const spam = await antiSpam(fd, 'candidature')
  if (spam === 'spam') return { status: 'success', message: okMessage }
  if (spam) return spam

  const parsed = applicationSchema.safeParse(asObject(fd, ['name', 'email', 'phone', 'position', 'linkedin', 'message', 'jobId', 'consent']))
  const fieldErrors = parsed.success ? {} : toFieldErrors(parsed.error.issues)

  const file = fd.get('cv')
  let cv: { buffer: Buffer; ext: string; mime: string } | null = null
  if (!(file instanceof File) || file.size === 0) {
    fieldErrors.cv = 'Joignez votre CV (PDF ou Word).'
  } else if (file.size > CV_MAX_BYTES) {
    fieldErrors.cv = 'Le fichier dépasse 5 Mo.'
  } else {
    const buffer = Buffer.from(await file.arrayBuffer())
    const type = detectCvType(buffer)
    if (!type) fieldErrors.cv = 'Format non accepté : PDF, DOC ou DOCX uniquement.'
    else cv = { buffer, ...type }
  }
  if (!parsed.success || Object.keys(fieldErrors).length > 0 || !cv) {
    return { status: 'error', message: 'Certains champs sont à corriger.', fieldErrors }
  }
  const d = parsed.data

  try {
    const payload = await getPayloadClient()
    let jobId: number | undefined
    let jobTitle: string | undefined
    if (d.jobId) {
      const job = await payload.find({ collection: 'offres', where: { and: [{ id: { equals: Number(d.jobId) } }, { status: { equals: 'ouverte' } }] }, limit: 1, depth: 0 })
      if (job.docs[0]) {
        jobId = job.docs[0].id as number
        jobTitle = job.docs[0].title
      }
    }
    const fileName = `cv-${new Date().toISOString().slice(0, 10)}-${randomBytes(6).toString('hex')}.${cv.ext}`
    const cvDoc = await payload.create({
      collection: 'cv',
      overrideAccess: true,
      data: {},
      file: { data: cv.buffer, mimetype: cv.mime, name: fileName, size: cv.buffer.length },
    })
    const doc = await payload.create({
      collection: 'candidatures',
      overrideAccess: true,
      data: {
        name: d.name,
        email: d.email,
        phone: d.phone || undefined,
        position: d.position || jobTitle || undefined,
        linkedin: d.linkedin || undefined,
        message: d.message,
        job: jobId,
        cv: cvDoc.id,
        consentAt: new Date().toISOString(),
        treatment: 'nouveau',
      },
    })
    const sent = await notify(
      `[Site] Candidature — ${jobTitle || d.position || 'spontanée'}`,
      [
        ['Nom', d.name],
        ['E-mail', d.email],
        ['Téléphone', d.phone],
        ['Offre', jobTitle || 'Candidature spontanée'],
        ['Poste souhaité', d.position],
        ['Profil en ligne', d.linkedin],
        ['Message', d.message],
        ['CV', 'Disponible dans l’administration (Carrières › Candidatures)'],
      ],
      d.email,
    )
    if (sent) await payload.update({ collection: 'candidatures', id: doc.id, overrideAccess: true, data: { emailNotified: true } })
    return { status: 'success', message: okMessage }
  } catch (err) {
    const payload = await getPayloadClient()
    payload.logger.error({ msg: '[candidature] enregistrement impossible', err: (err as Error).name })
    return { status: 'error', message: GENERIC_ERROR }
  }
}
